import { useEffect, useMemo, useRef } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Input, Label, Select } from '@kmgestao/ui';

import {
  LEAD_SOURCES,
  LEAD_SOURCE_LABELS,
  MEDICAL_SPECIALTIES,
  SPECIALTY_LABELS,
  formatCep,
  formatPhoneBR,
  maskCnpjProgressive,
  normalizeCep,
  normalizeCnpj,
  toTitleCase,
  useCepLookup,
  useCnpjLookup,
  type CreateLeadInput,
} from '../../../features/leads/index.js';

export interface LeadFormFieldsProps {
  form: UseFormReturn<CreateLeadInput>;
  /** Em modo edição, o usuário já digitou os dados antes — autofill do
   * CNPJ NÃO deve sobrescrever valores existentes. `respectExistingValues`
   * garante isso. Em criação é false (não tem nada pra preservar). */
  respectExistingValues?: boolean;
  /** Opcional: id prefix pros inputs (evita colisão se 2 forms na mesma página).
   * Default `"lead-form"`. */
  idPrefix?: string;
}

/**
 * Campos compartilhados entre `NewLeadDialog` e `EditLeadDialog` — mesmo
 * schema, mesmo form, mesmo comportamento de autofill por CNPJ.
 *
 * **Layout mobile-first**: todos os campos em grid 1-col no mobile,
 * 2-cols em `sm+`. Seção de endereço tem grid 3-cols em `lg+` para CEP
 * + número + complemento caberem em linha.
 *
 * **Autofill**: quando CNPJ tem 14 dígitos válidos, busca BrasilAPI e
 * popula `legal_name`, `clinic_name_guess`, `phone`, `email`, `address.*`
 * — mas SÓ SOBRESCREVE campos vazios (respeita edição manual do user).
 * Em modo edição (`respectExistingValues`), nem preenche campos vazios
 * se o lead já existe — apenas confirma que o CNPJ é válido.
 */
export function LeadFormFields({
  form,
  respectExistingValues = false,
  idPrefix = 'lead-form',
}: LeadFormFieldsProps) {
  const {
    register,
    setValue,
    watch,
    getValues,
    formState: { errors },
  } = form;

  const cnpjRaw = watch('cnpj');
  const cnpjDigits = normalizeCnpj(cnpjRaw);
  const cnpjQuery = useCnpjLookup(cnpjDigits);

  // Autofill por CEP — hook espelhando o `useCnpjLookup`. Dispara
  // quando `address.cep` tem 8 dígitos válidos. Usado tanto em
  // create quanto em edit: digitar um CEP novo é um SINAL EXPLÍCITO
  // do user de "quero este endereço".
  const cepRaw = watch('address.cep');
  const cepDigits = normalizeCep(cepRaw);
  const cepQuery = useCepLookup(cepDigits);

  // Captura o CEP que estava no form no primeiro render — em modo edit,
  // é o CEP do lead carregado do banco. Usamos isso para NÃO aplicar
  // autofill em cima do endereço que já existe (o user pode ter
  // customizado manualmente). Só quando o user DIGITA um CEP novo
  // (diferente do inicial), o autofill sobrescreve street/district/
  // city/uf. Em create mode, `formCepInitial.current` nasce vazio e
  // qualquer CEP válido dispara autofill normalmente.
  const formCepInitial = useRef<string | undefined>(undefined);
  if (formCepInitial.current === undefined) {
    formCepInitial.current = normalizeCep(getValues('address.cep'));
  }

  // Autofill quando a busca CNPJ retorna — aplica a padronização do
  // sistema (Title Case em nomes, formatCep no CEP, uppercase em UF,
  // lowercase em email). Regra: BrasilAPI devolve tudo em CAIXA ALTA
  // (padrão Receita Federal); ao preencher, já deixamos no formato
  // final que vai aparecer no contrato. Respeita valores existentes.
  useEffect(() => {
    if (!cnpjQuery.data || respectExistingValues) return;
    const d = cnpjQuery.data;
    const current = watch();

    if (!current.legal_name && d.razao_social) {
      setValue('legal_name', toTitleCase(d.razao_social), { shouldValidate: true });
    }
    if (!current.clinic_name_guess && (d.nome_fantasia || d.razao_social)) {
      setValue('clinic_name_guess', toTitleCase(d.nome_fantasia ?? d.razao_social ?? ''), {
        shouldValidate: true,
      });
    }
    if (!current.phone && d.ddd_telefone_1) {
      setValue('phone', formatPhoneBR(d.ddd_telefone_1), { shouldValidate: true });
    }
    if (!current.email && d.email) {
      setValue('email', d.email.trim().toLowerCase(), { shouldValidate: true });
    }

    const addr = current.address ?? {};
    if (!addr.cep && d.cep) setValue('address.cep', formatCep(d.cep));
    if (!addr.street && d.logradouro) {
      setValue('address.street', toTitleCase(d.logradouro));
    }
    if (!addr.number && d.numero) setValue('address.number', d.numero.trim());
    if (!addr.complement && d.complemento) {
      setValue('address.complement', toTitleCase(d.complemento));
    }
    if (!addr.district && d.bairro) {
      setValue('address.district', toTitleCase(d.bairro));
    }
    if (!addr.city && d.municipio) {
      setValue('address.city', toTitleCase(d.municipio));
    }
    if (!addr.uf && d.uf) setValue('address.uf', d.uf.toUpperCase().slice(0, 2));
  }, [cnpjQuery.data, respectExistingValues, setValue, watch]);

  // Autofill por CEP — dispara quando o user digita um CEP novo (8
  // dígitos). BrasilAPI v2 retorna `street`, `neighborhood`, `city`,
  // `state` (UF). Aplica padronização do sistema (Title Case em nomes,
  // UPPERCASE em UF). Em modo edit, IGNORA o primeiro resultado quando
  // bate com o CEP que já estava no lead (evita sobrescrever endereço
  // customizado no load). Em edit, trocar o CEP sobrescreve; em create,
  // qualquer CEP válido sobrescreve.
  //
  // Não toca em `number` nem `complement` — esses são características
  // do imóvel (sempre manual).
  useEffect(() => {
    if (!cepQuery.data) return;
    if (respectExistingValues && cepDigits === formCepInitial.current) return;

    const d = cepQuery.data;
    if (d.street) {
      setValue('address.street', toTitleCase(d.street), { shouldValidate: true });
    }
    if (d.neighborhood) {
      setValue('address.district', toTitleCase(d.neighborhood), { shouldValidate: true });
    }
    if (d.city) {
      setValue('address.city', toTitleCase(d.city), { shouldValidate: true });
    }
    if (d.state) {
      setValue('address.uf', d.state.toUpperCase().slice(0, 2), { shouldValidate: true });
    }
  }, [cepQuery.data, cepDigits, respectExistingValues, setValue]);

  const addressPreview = useMemo(() => {
    const d = cnpjQuery.data;
    if (!d) return null;
    const parts = [d.logradouro, d.numero, d.bairro, d.municipio, d.uf, d.cep].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  }, [cnpjQuery.data]);

  return (
    <div className="space-y-6">
      {/* Seção 1 — CNPJ (dispara autofill) + Razão social + Nome fantasia */}
      <section className="space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Identificação
        </h3>

        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-cnpj`}>CNPJ</Label>
          <Input
            id={`${idPrefix}-cnpj`}
            {...register('cnpj')}
            placeholder="00.000.000/0000-00"
            inputMode="numeric"
            autoComplete="off"
            maxLength={18}
            hasError={!!errors.cnpj}
            onChange={(e) => {
              const raw = normalizeCnpj(e.target.value);
              setValue('cnpj', maskCnpjProgressive(raw), { shouldValidate: true });
            }}
          />
          {errors.cnpj ? (
            <p className="text-sm text-destructive" role="alert">
              {errors.cnpj.message}
            </p>
          ) : cnpjQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Consultando Receita Federal…</p>
          ) : cnpjQuery.isError ? (
            <p className="text-sm text-destructive" role="alert">
              {(cnpjQuery.error as Error).message}
            </p>
          ) : cnpjQuery.data ? (
            <div className="rounded-md border border-border bg-muted/50 p-3 text-xs text-muted-foreground">
              <p className="text-foreground">
                <strong>{cnpjQuery.data.razao_social ?? '—'}</strong>
                {cnpjQuery.data.descricao_situacao_cadastral
                  ? ` · ${cnpjQuery.data.descricao_situacao_cadastral}`
                  : ''}
              </p>
              {addressPreview ? <p className="mt-1">{addressPreview}</p> : null}
              <p className="mt-2 text-xs italic">
                Dados da Receita Federal — confira e ajuste se estiverem desatualizados.
              </p>
            </div>
          ) : null}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-legal-name`}>Razão social</Label>
            <Input
              id={`${idPrefix}-legal-name`}
              {...register('legal_name')}
              placeholder="Auto-preenchido pelo CNPJ"
              autoComplete="organization"
              onBlur={(e) => {
                setValue('legal_name', toTitleCase(e.target.value), {
                  shouldValidate: true,
                });
              }}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-clinic-name`}>Nome fantasia</Label>
            <Input
              id={`${idPrefix}-clinic-name`}
              {...register('clinic_name_guess')}
              placeholder="Nome comercial / da clínica"
              autoComplete="organization"
              onBlur={(e) => {
                setValue('clinic_name_guess', toTitleCase(e.target.value), {
                  shouldValidate: true,
                });
              }}
            />
          </div>
        </div>
      </section>

      {/* Seção 2 — Contato */}
      <section className="space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Contato
        </h3>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-fullname`}>Nome do responsável *</Label>
            <Input
              id={`${idPrefix}-fullname`}
              {...register('full_name')}
              placeholder="Dr(a). Maria Silva"
              autoComplete="name"
              hasError={!!errors.full_name}
              onBlur={(e) => {
                setValue('full_name', toTitleCase(e.target.value), {
                  shouldValidate: true,
                });
              }}
            />
            {errors.full_name ? (
              <p className="text-sm text-destructive" role="alert">
                {errors.full_name.message}
              </p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-email`}>Email de contato *</Label>
            <Input
              id={`${idPrefix}-email`}
              type="email"
              inputMode="email"
              autoComplete="email"
              {...register('email')}
              placeholder="contato@clinica.com.br"
              hasError={!!errors.email}
            />
            {errors.email ? (
              <p className="text-sm text-destructive" role="alert">
                {errors.email.message}
              </p>
            ) : null}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-phone`}>Telefone</Label>
            <Input
              id={`${idPrefix}-phone`}
              {...register('phone')}
              placeholder="(11) 99999-9999"
              inputMode="tel"
              autoComplete="tel"
              maxLength={15}
              onChange={(e) => {
                setValue('phone', formatPhoneBR(e.target.value), { shouldValidate: true });
              }}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-specialty`}>Especialidade</Label>
            <Select id={`${idPrefix}-specialty`} {...register('specialty')}>
              <option value="">— Selecionar —</option>
              {MEDICAL_SPECIALTIES.map((s) => (
                <option key={s} value={s}>
                  {SPECIALTY_LABELS[s]}
                </option>
              ))}
            </Select>
          </div>
        </div>
      </section>

      {/* Seção 3 — Endereço (vem do BrasilAPI via autofill + editável) */}
      <section className="space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Endereço cadastral
        </h3>
        <p className="-mt-2 text-xs text-muted-foreground">
          Preenchido automaticamente pelo CNPJ. Pode ser corrigido se a Receita estiver
          desatualizada ou a clínica operar em endereço diferente.
        </p>

        <div className="grid gap-4 sm:grid-cols-[8rem,1fr,6rem] lg:grid-cols-[8rem,1fr,7rem,1fr]">
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-cep`}>CEP</Label>
            <Input
              id={`${idPrefix}-cep`}
              {...register('address.cep')}
              placeholder="00000-000"
              inputMode="numeric"
              autoComplete="postal-code"
              maxLength={9}
              onChange={(e) => {
                // Máscara progressiva — já formata enquanto digita.
                // Quando atinge 8 dígitos, o `useCepLookup` dispara
                // automaticamente (via `watch` + `enabled: length === 8`).
                setValue('address.cep', formatCep(e.target.value), {
                  shouldValidate: true,
                });
              }}
            />
            {cepQuery.isLoading ? (
              <p className="text-xs text-muted-foreground">Consultando CEP…</p>
            ) : cepQuery.isError ? (
              <p className="text-xs text-destructive" role="alert">
                {(cepQuery.error as Error).message}
              </p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-street`}>Logradouro</Label>
            <Input
              id={`${idPrefix}-street`}
              {...register('address.street')}
              placeholder="Rua, avenida, travessa…"
              autoComplete="street-address"
              onBlur={(e) => {
                setValue('address.street', toTitleCase(e.target.value), {
                  shouldValidate: true,
                });
              }}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-number`}>Número</Label>
            <Input
              id={`${idPrefix}-number`}
              {...register('address.number')}
              placeholder="nº"
              inputMode="numeric"
              autoComplete="off"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-complement`}>Complemento</Label>
            <Input
              id={`${idPrefix}-complement`}
              {...register('address.complement')}
              placeholder="sala, bloco (opcional)"
              autoComplete="off"
              onBlur={(e) => {
                setValue('address.complement', toTitleCase(e.target.value), {
                  shouldValidate: true,
                });
              }}
            />
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-[1fr,1fr,6rem]">
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-district`}>Bairro</Label>
            <Input
              id={`${idPrefix}-district`}
              {...register('address.district')}
              placeholder="Centro, etc."
              autoComplete="address-level3"
              onBlur={(e) => {
                setValue('address.district', toTitleCase(e.target.value), {
                  shouldValidate: true,
                });
              }}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-city`}>Cidade</Label>
            <Input
              id={`${idPrefix}-city`}
              {...register('address.city')}
              placeholder="São Paulo"
              autoComplete="address-level2"
              onBlur={(e) => {
                setValue('address.city', toTitleCase(e.target.value), {
                  shouldValidate: true,
                });
              }}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-uf`}>UF</Label>
            <Input
              id={`${idPrefix}-uf`}
              {...register('address.uf')}
              placeholder="SP"
              autoComplete="address-level1"
              maxLength={2}
              onChange={(e) => {
                setValue('address.uf', e.target.value.toUpperCase().slice(0, 2), {
                  shouldValidate: true,
                });
              }}
            />
          </div>
        </div>
      </section>

      {/* Seção 4 — Origem + Notas */}
      <section className="space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Classificação interna
        </h3>

        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-source`}>Origem</Label>
          <Select id={`${idPrefix}-source`} {...register('source')}>
            {LEAD_SOURCES.map((s) => (
              <option key={s} value={s}>
                {LEAD_SOURCE_LABELS[s]}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-notes`}>Notas internas (opcional)</Label>
          <textarea
            id={`${idPrefix}-notes`}
            {...register('notes')}
            rows={3}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
            placeholder="Contexto relevante — reuniões agendadas, indicação de quem, etc."
          />
        </div>
      </section>
    </div>
  );
}
